import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/utils";
import { inr, themeWord, meditationActions as act } from "@/components/site/MeditationBits";
import { MeditationCheckoutHost } from "@/components/site/MeditationCheckout";

// Homepage-only card lines; names, themes, images and prices come from Admin.
const HOME_LINES = {
  "open-eye-focus": "Come back to the world in front of you.",
  "anand-dhyan": "Reconnect with the warmth and joy within you.",
  "sankalp-dhyan": "Give your deepest intentions a conscious place within you.",
  "aekam-dhyan": "Turn your attention towards the stillness within.",
};

function HomeMedCard({ m }) {
  const img = resolveImg(m.image_url);
  return (
    <article className="soft-card !p-0 overflow-hidden flex flex-col" data-testid={`home-med-card-${m.slug}`}>
      <div className="relative aspect-[16/10] bg-[#F3EFEA]">
        {img ? <img src={img} alt={m.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-testid={`home-med-image-${m.slug}`} />
          : <span className="absolute inset-0 flex items-center justify-center font-serif text-4xl text-[#E07A5F]/30">{themeWord(m.theme)}</span>}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[11px] uppercase tracking-[0.25em] text-terracotta">{m.theme}</p>
        <h3 className="font-serif text-2xl text-[#2D2422] mt-2 leading-tight">{m.name}</h3>
        <p className="mt-2 text-[15px] text-[#3D3633] leading-relaxed flex-1">{HOME_LINES[m.slug] || m.short_line}</p>
        <p className="mt-4 text-base font-semibold text-[#4A3B32]" data-testid={`home-med-price-${m.slug}`}>{inr(m.price_inr)}</p>
        <Link to={`/meditations/${m.slug}`} className="btn-ghost mt-4 w-full" data-testid={`home-explore-${m.slug}`}>Explore {themeWord(m.theme)}</Link>
      </div>
    </article>
  );
}

function HomeCollectionOffer({ col }) {
  return (
    <div className="mt-8 rounded-[2rem] bg-[#4A3B32] text-[#EBE5D9] px-7 sm:px-12 py-10 sm:py-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-7" data-testid="home-collection-offer">
      <div>
        <h3 className="font-serif text-3xl sm:text-4xl text-white">Not sure where to begin?</h3>
        <p className="mt-3 text-[#D4C9BE] text-base sm:text-lg">Experience all four iChaitanya guided meditations.</p>
      </div>
      <div className="flex flex-col items-stretch sm:items-start lg:items-end gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/meditations" className="btn-ghost !bg-transparent !text-white !border-white/40 hover:!bg-white/10" data-testid="home-explore-meditations">Explore All Meditations <ArrowRight size={15} className="ml-2" /></Link>
          <button onClick={() => act.buyCollection(col)} className="btn-primary" data-testid="home-get-all-four">Get All Four — {inr(col.price_inr)}</button>
        </div>
        <p className="text-sm text-[#D4C9BE]">One-time purchase.</p>
      </div>
    </div>
  );
}

export function HomeMeditations() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/meditations").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data || !data.meditations.length) return null;
  const meds = data.meditations, col = data.collection;
  return (
    <section className="container-ic py-16 sm:py-20" data-testid="home-meditations">
      <h2 className="font-serif text-4xl sm:text-5xl">What do you need today?</h2>
      <p className="mt-4 text-[#5C5955] text-base sm:text-lg">Four guided meditations.<br />Four different ways of turning inward.</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">{meds.map(m => <HomeMedCard key={m.id} m={m} />)}</div>
      {col && <HomeCollectionOffer col={col} />}
      <MeditationCheckoutHost includes={meds.map(m => m.name)} />
    </section>
  );
}
